import type { TestimonialsSectionContent, CarouselConfig, ScrollRevealConfig } from "@site-factory/config";
import { SectionTitle, SectionSubtitle } from "../typography/Typography";
import { Card, CardContent } from "../components/Card";
import { BandedSection, type BandedSectionVariant } from "../components/BandedSection";
import { Carousel } from "../components/Carousel";
import { AnimateOnScroll, AnimatedChild } from "../animations";
import { StarRating } from "../components/StarRating";
import { Skeleton, SkeletonAvatar, SkeletonText } from "../components/Skeleton";

export interface TestimonialsSectionProps extends TestimonialsSectionContent {
  className?: string;
  bandVariant?: BandedSectionVariant;
  /** Carousel configuration (only applies to carousel variant) */
  carouselConfig?: CarouselConfig;
  /** Optional scroll reveal animation configuration */
  scrollReveal?: ScrollRevealConfig;
  /** Show skeleton placeholders while loading */
  loading?: boolean;
}

export function TestimonialsSection({
  title,
  subtitle,
  testimonials,
  variant = "grid",
  className,
  bandVariant = "plain",
  carouselConfig = {
    autoplay: true,
    interval: 5000,
    loop: true,
    showArrows: true,
    showDots: true,
    slidesToShow: { mobile: 1, tablet: 2, desktop: 3 },
  },
  scrollReveal = { enabled: true, variant: "fadeUp", staggerChildren: 0.1 },
  loading = false,
}: TestimonialsSectionProps) {
  const skeletons = Array.from({ length: 3 });

  const TestimonialCard = ({ testimonial }: { testimonial: typeof testimonials[0] }) => (
    <Card className="p-6 shadow-sm-2 hover:shadow-md-2 transition h-full border border-neutral-200/70 dark:border-white/10">
      <CardContent className="p-0 space-y-3">
        <blockquote
          className="text-lg italic"
          style={{ color: "var(--color-text, #0f172a)", opacity: 0.9 }}
        >
          "{testimonial.quote}"
        </blockquote>
        <div className="flex items-center gap-3 mt-4">
          {testimonial.avatar && (
            <img
              src={testimonial.avatar}
              alt={testimonial.author}
              className="w-12 h-12 rounded-full object-cover"
            />
          )}
          <div>
            <p
              className="font-semibold"
              style={{ color: "var(--color-text, #0f172a)" }}
            >
              {testimonial.author}
            </p>
            {testimonial.role && (
              <p
                className="text-sm"
                style={{ color: "var(--color-text, #0f172a)", opacity: 0.75 }}
              >
                {testimonial.role}
              </p>
            )}
            {typeof testimonial.rating === "number" && (
              <div className="mt-1">
                <StarRating value={testimonial.rating} size="sm" />
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const SkeletonCard = () => (
    <Card className="p-6 bg-white/90 shadow-sm-2">
      <CardContent className="p-0 space-y-4">
        <SkeletonText lines={3} />
        <div className="flex items-center gap-3 mt-4">
          <SkeletonAvatar size={48} />
          <div className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <BandedSection variant={bandVariant} className="py-16 md:py-24" contentClassName={className}>
      {(title || subtitle) && (
        <AnimateOnScroll variant="fadeDown" duration={0.5}>
          <div className="text-center mb-12">
            {title && <SectionTitle>{title}</SectionTitle>}
            {subtitle && <SectionSubtitle className="mt-4">{subtitle}</SectionSubtitle>}
          </div>
        </AnimateOnScroll>
      )}

      {loading ? (
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {skeletons.map((_, idx) => (
            <SkeletonCard key={idx} />
          ))}
        </div>
      ) : variant === "carousel" ? (
        <Carousel {...carouselConfig} ariaLabel="Customer testimonials">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index} testimonial={testimonial} />
          ))}
        </Carousel>
      ) : (
        <AnimateOnScroll
          className="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
          {...scrollReveal}
        >
          {testimonials.map((testimonial, index) => (
            <AnimatedChild key={index}>
              <TestimonialCard testimonial={testimonial} />
            </AnimatedChild>
          ))}
        </AnimateOnScroll>
      )}
    </BandedSection>
  );
}
